// Form mode for Brand Genius - displays all fields in an organized form layout
import React, { useState, useEffect } from 'react';
import { Tables } from '../../../database.types';
import fieldMetadata from '../../lib/fieldMetadata.json';
import AIGenerateDialog from './AIGenerateDialog';
import { BrandProgress } from './BrandProgress';
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Copy, ChevronDown, Sparkles, ChevronUp, Eye, EyeOff, Lightbulb, RefreshCcw, ClipboardCopy, CheckCircle2 } from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from "@/components/ui/dialog"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { motion, AnimatePresence } from 'framer-motion'
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { ToastSave } from "@/components/ui/toast-save"
import { toast } from "@/components/ui/use-toast"
import { supabase } from '@/lib/supabase'

type Brand = Tables<'brands'>;

const getFieldValue = (brand: Brand | null, key: keyof Brand): string => {
    if (!brand) return '';
    const value = brand[key];
    return value === null || value === undefined ? '' : String(value);
};

interface BrandGeniusFormProps {
    localCurrentBrand: Brand | null;
    setLocalCurrentBrand: React.Dispatch<React.SetStateAction<Brand | null>>;
    updateBrandField: (brandId: string, field: string, value: string) => Promise<void>;
}

export const BrandGeniusForm: React.FC<BrandGeniusFormProps> = ({
    localCurrentBrand,
    setLocalCurrentBrand,
    updateBrandField
}) => {
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [currentField, setCurrentField] = useState('');
    const [unsavedChanges, setUnsavedChanges] = useState<Record<string, boolean>>({});
    const [openCategories, setOpenCategories] = useState<string[]>([]);
    const [fieldGroups, setFieldGroups] = useState<Record<string, string[]>>({});
    const [showPopulated, setShowPopulated] = useState(true);
    const [saveState, setSaveState] = useState<'initial' | 'loading' | 'success'>('initial');
    const [showChoiceModal, setShowChoiceModal] = useState(false);
    const [isFeedbackMode, setIsFeedbackMode] = useState(false);
    const [brandGeniusFeedback, setBrandGeniusFeedback] = useState('');
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [savingFields, setSavingFields] = useState<Record<string, boolean>>({});
    const [savedFields, setSavedFields] = useState<Record<string, boolean>>({});

    const hasUnsavedChanges = Object.values(unsavedChanges).some(Boolean);

    // Build field groups
    useEffect(() => {
        const groups: Record<string, string[]> = {};
        Object.entries(fieldMetadata).forEach(([key, value]) => {
            const group = value.group || 'Other';
            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(key);
        });
        setFieldGroups(groups);
    }, []);

    useEffect(() => {
        if (localCurrentBrand) {
            // Get all categories that have empty fields OR unsaved changes
            const categoriesToOpen = Object.entries(fieldGroups).reduce((acc, [group, fields]) => {
                const hasEmptyField = fields.some(key => {
                    const value = localCurrentBrand[key as keyof Brand];
                    return value === null || value === undefined || value === '';
                });

                const hasUnsavedChanges = fields.some(key => unsavedChanges[key]);

                if (hasEmptyField || hasUnsavedChanges) {
                    acc.push(group);
                }
                return acc;
            }, [] as string[]);

            setOpenCategories(categoriesToOpen);
        }
    }, [localCurrentBrand, fieldGroups, unsavedChanges]);

    const formatLabel = (key: string) => {
        const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
        return metadata?.name || key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const isGeneratableField = (key: string) => {
        // System fields that should never be generated
        const nonGeneratableFields = ['id', 'createdAt', 'updatedAt', 'wordpress_password'];
        if (nonGeneratableFields.includes(key)) return false;
        
        // Check if field exists in metadata and allows AI generation
        const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
        return metadata && metadata.allowAiGeneration === true;
    };

    const isEditableField = (key: string) => {
        const nonEditableFields = ['id', 'createdAt', 'updatedAt'];
        return !nonEditableFields.includes(key);
    };

    const openGenerateModal = (field: string) => {
        setCurrentField(field);
        
        if (localCurrentBrand) {
            const currentValue = localCurrentBrand[field as keyof Brand];
            
            if (currentValue && String(currentValue).trim() !== '') {
                // If the field already has a value, show the choice modal
                setShowChoiceModal(true);
            } else {
                // Otherwise show the AI dialog directly
                setIsFeedbackMode(false);
                setShowGenerateModal(true);
            }
        } else {
            // Fallback if brand data isn't loaded
            setShowGenerateModal(true);
        }
    };

    const handleInput = (key: string, value: string) => {
        if (localCurrentBrand) {
            setLocalCurrentBrand({ ...localCurrentBrand, [key]: value });
            setUnsavedChanges({ ...unsavedChanges, [key]: true });
        }
    };

    const applyGeneratedContent = (fieldName: string, content: string) => {
        if (localCurrentBrand) {
            setLocalCurrentBrand({ ...localCurrentBrand, [fieldName]: content });
            setUnsavedChanges({ ...unsavedChanges, [fieldName]: true });
        }
    };

    const handleSaveField = async (key: string) => {
        if (!localCurrentBrand || !unsavedChanges[key]) return;

        // Set saving state for this field
        setSavingFields(prev => ({ ...prev, [key]: true }));
        
        try {
            const value = getFieldValue(localCurrentBrand, key as keyof Brand);
            await updateBrandField(localCurrentBrand.id, key, value);
            
            // Remove from unsaved changes
            setUnsavedChanges(prev => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
            
            // Set saving to false and saved to true
            setSavingFields(prev => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
            
            setSavedFields(prev => ({ ...prev, [key]: true }));
            
            // Clear saved indicator after 2 seconds
            setTimeout(() => {
                setSavedFields(prev => {
                    const newState = { ...prev };
                    delete newState[key];
                    return newState;
                });
            }, 2000);
            
            // Trigger convergence processing
            try {
                const session = await supabase.auth.getSession();
                if (session.data.session) {
                    const workerUrl = import.meta.env.VITE_WORKERS_URL;
                    const endpoint = `${workerUrl}/rainfactory/brand-genius/process_updated_brand`
                    await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${session.data.session.access_token}`
                        },
                        body: JSON.stringify({
                            brandId: localCurrentBrand.id
                        })
                    });
                }
            } catch (error) {
                console.error('Failed to process updated brand:', error);
                // Don't show an error for this as it's a background process
            }
            
            return true;
        } catch (error) {
            console.error(`Failed to save field ${key}:`, error);
            
            // Check if this is a provider outage
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isProviderOutage = 
                errorMessage.includes('service unavailable') || 
                errorMessage.includes('provider error') ||
                errorMessage.includes('insufficient_quota') ||
                (error as any)?.isProviderOutage;
            
            toast({
                title: isProviderOutage ? 'Service Unavailable' : 'Error Saving',
                description: isProviderOutage 
                    ? 'AI service is temporarily unavailable. Please try again later.'
                    : `Failed to save ${key.replace(/_/g, ' ')}. Please try again.`,
                variant: 'destructive',
            });
            
            // Clear saving state
            setSavingFields(prev => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
            
            return false;
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            toast({
                title: "Copied to clipboard",
                description: "The content has been copied to your clipboard.",
                duration: 2000,
            });
        }).catch((err) => {
            console.error('Failed to copy text: ', err);
            toast({
                title: "Failed to copy",
                description: "There was an error copying to your clipboard.",
                variant: "destructive",
            });
        });
    };

    const toggleCategory = (category: string) => {
        const groupFields = fieldGroups[category] || [];
        const hasUnsavedChanges = groupFields.some(key => unsavedChanges[key]);

        setOpenCategories(prev => {
            if (prev.includes(category)) {
                // Don't allow closing if there are unsaved changes
                if (hasUnsavedChanges) {
                    return prev;
                }
                return prev.filter(c => c !== category);
            }
            return [...prev, category];
        });
    };

    const filterFields = (fields: Record<string, any>) => {
        // First filter out hidden fields
        const visibleFields = Object.entries(fields).reduce((acc, [key, value]) => {
            const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
            if (!metadata?.hidden && key !== 'status') { // Exclude status field and hidden fields
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, any>);

        // Then apply the populated/unpopulated filter if needed
        if (showPopulated) return visibleFields;

        // Include both empty fields AND fields with unsaved changes in the filtered results
        return Object.entries(visibleFields).reduce((acc, [key, value]) => {
            if (!value || value === '' || unsavedChanges[key]) {
                // Always include the field's current value if it has unsaved changes
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, any>);
    };

    const filteredDetails = localCurrentBrand
        ? Object.entries(fieldGroups).reduce((acc, [group, fields]) => {
            const groupFields = fields;

            if (groupFields.length > 0) {
                // Get all values for the group's fields
                const groupValues = groupFields.reduce((groupAcc, key) => {
                    groupAcc[key] = localCurrentBrand[key as keyof Brand];
                    return groupAcc;
                }, {} as Record<string, Brand[keyof Brand]>);

                // Get filtered values based on showPopulated setting
                const filteredGroupValues = filterFields(groupValues);

                // Include the group if it has any fields that are either empty or have unsaved changes
                const hasUnsavedChanges = groupFields.some(key => unsavedChanges[key]);
                const hasEmptyFields = groupFields.some(key => {
                    const value = localCurrentBrand[key as keyof Brand];
                    return !value || value === '';
                });

                // When showPopulated is true, show all groups
                // When showPopulated is false, only show groups with empty fields or unsaved changes
                const shouldShowGroup = showPopulated || hasUnsavedChanges || hasEmptyFields;

                // Only add the group if there are actually fields to show
                if (shouldShowGroup) {
                    // If there are unsaved changes, include all fields in the group that have unsaved changes
                    if (hasUnsavedChanges) {
                        groupFields.forEach(key => {
                            if (unsavedChanges[key]) {
                                filteredGroupValues[key] = groupValues[key];
                            }
                        });
                    }

                    // Only add the group if it has visible fields after filtering
                    if (Object.keys(filteredGroupValues).length > 0) {
                        acc[group] = filteredGroupValues;
                    }
                }
            }
            return acc;
        }, {} as Record<string, Partial<Brand>>)
        : {};

    const calculateSectionProgress = (fields: Partial<Brand>) => {
        const visibleFields = Object.keys(fields).filter(key => {
            const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
            return !metadata?.hidden;
        });
        const totalFields = visibleFields.length;
        const filledFields = visibleFields.filter(key => {
            const value = fields[key as keyof Brand];
            return value !== null && value !== undefined && value !== '';
        }).length;
        return totalFields === 0 ? 0 : (filledFields / totalFields) * 100;
    };

    const getFieldComponent = (key: string, value: string, onChange: (value: string) => void, disabled: boolean = false) => {
        const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
        const inputType = metadata?.inputControl?.toLowerCase() || '';
        const className = `${unsavedChanges[key] ? 'border-blue-500 bg-blue-50/50' : ''}`;

        const handleBlur = () => {
            if (unsavedChanges[key]) {
                handleSaveField(key);
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && inputType !== 'textarea' && unsavedChanges[key]) {
                handleSaveField(key);
            }
        };

        if (inputType.includes('textarea')) {
            return (
                <Textarea
                    id={key}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={handleBlur}
                    disabled={disabled}
                    className={`min-h-[100px] ${className}`}
                />
            );
        } else if (inputType.includes('select')) {
            return (
                <div className="flex flex-wrap gap-2">
                    {metadata.options?.map((option) => (
                        <Button
                            key={option}
                            variant={value === option ? "default" : "outline"}
                            onClick={() => {
                                onChange(option);
                                // Save immediately after selection
                                setTimeout(() => handleSaveField(key), 0);
                            }}
                            disabled={disabled}
                            className={`${value === option ? 'bg-primary text-primary-foreground' : ''}`}
                        >
                            {option}
                        </Button>
                    ))}
                </div>
            );
        } else {
            const type = inputType.includes('url') ? 'url'
                : inputType.includes('password') ? 'password'
                    : inputType.includes('number') ? 'number'
                        : 'text';

            return (
                <Input
                    id={key}
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || inputType.includes('disabled') || inputType.includes('hidden')}
                    className={className}
                />
            );
        }
    };

    const handleSaveAll = async () => {
        if (!localCurrentBrand) return;
        
        setSaveState('loading');
        const fields = Object.keys(unsavedChanges).filter(key => unsavedChanges[key]);
        
        if (fields.length === 0) {
            setSaveState('success');
            setTimeout(() => setSaveState('initial'), 2000);
            return;
        }
        
        try {
            // Save all unsaved fields
            await Promise.all(fields.map(field => handleSaveField(field)));
            
            setSaveState('success');
            
            // Show success toast
            toast({
                title: "All changes saved",
                description: "Your brand details have been updated",
                duration: 2000,
            });
            
            // Reset success state after 2 seconds
            setTimeout(() => setSaveState('initial'), 2000);
        } catch (error) {
            console.error('Failed to save all changes:', error);
            
            // Check if this is a provider outage
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isProviderOutage = 
                errorMessage.includes('service unavailable') || 
                errorMessage.includes('provider error') ||
                errorMessage.includes('insufficient_quota') ||
                (error as any)?.isProviderOutage;
            
            toast({
                title: isProviderOutage ? 'Service Unavailable' : 'Error Saving',
                description: isProviderOutage 
                    ? 'AI service is temporarily unavailable. Please try again later.'
                    : 'Failed to save all changes. Please try again.',
                variant: 'destructive',
            });
            
            setSaveState('initial');
        }
    };

    const handleResetAll = () => {
        // This would need to be passed from parent to reset to last saved state
        setUnsavedChanges({});
        setSavingFields({});
        setSavedFields({});
        
        toast({
            title: "Changes discarded",
            description: "All unsaved changes have been discarded",
            duration: 2000,
        });
    };

    // Add this new function to generate the summary text
    const generateBrandSummary = () => {
        if (!localCurrentBrand) return '';
        
        let summary = '';
        Object.entries(fieldGroups).forEach(([group, fields]) => {
            // Skip the System and Website Management groups
            if (group === 'System' || group === 'Website Management') return;
            
            const populatedFields = fields.filter(key => {
                const value = localCurrentBrand[key as keyof Brand];
                return value !== null && value !== undefined && value !== '';
            });
            
            if (populatedFields.length > 0) {
                summary += `\n## ${group}\n\n`;
                populatedFields.forEach(key => {
                    const value = localCurrentBrand[key as keyof Brand];
                    const label = formatLabel(key);
                    summary += `### ${label}\n${value}\n\n`;
                });
            }
        });
        
        return summary.trim();
    };

    return (
        <div className="flex flex-col gap-4">
            <BrandProgress brand={localCurrentBrand} showLink={false} />

            {/* Add desktop toast */}
            {hasUnsavedChanges && (
                <div className="hidden md:block">
                    <ToastSave
                        state={saveState}
                        onSave={handleSaveAll}
                        onReset={handleResetAll}
                        loadingText="Saving changes..."
                        successText="All changes saved"
                        initialText="You have unsaved changes"
                        resetText="Cancel"
                        saveText="Save"
                    />
                </div>
            )}

            {/* Update mobile toast to only show on mobile */}
            {hasUnsavedChanges && (
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 md:hidden">
                    <ToastSave
                        state={saveState}
                        onSave={handleSaveAll}
                        onReset={handleResetAll}
                        loadingText="Saving changes..."
                        successText="All changes saved"
                        initialText="You have unsaved changes"
                        resetText="Cancel"
                        saveText="Save"
                    />
                </div>
            )}

            <div className="flex items-center justify-between bg-muted/50 p-4 rounded-lg">
                <div>
                    <h3 className="text-sm font-medium">
                        {showPopulated
                            ? 'Showing all fields'
                            : `${Object.values(filteredDetails).reduce((acc, group) => acc + Object.keys(group).length, 0)} fields need attention`
                        }
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Use the filter below to focus on specific fields that need your attention.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPopulated(!showPopulated)}
                    className="flex items-center gap-2"
                >
                    {showPopulated ? (
                        <>
                            <EyeOff className="h-4 w-4" />
                            Show Empty Only
                        </>
                    ) : (
                        <>
                            <Eye className="h-4 w-4" />
                            Show All Fields
                        </>
                    )}
                </Button>
            </div>

            <ScrollArea className="h-[calc(100vh-16rem)] rounded-lg border">
                <div className="p-4 space-y-4">
                    {Object.entries(filteredDetails).map(([group, fields]) => (
                        <Card key={group}>
                            <CardHeader
                                className="cursor-pointer"
                                onClick={() => toggleCategory(group)}
                            >
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg font-medium">
                                        {group}
                                    </CardTitle>
                                    <div className="flex items-center gap-2">
                                        <div className="w-[60px] bg-muted-foreground/20 rounded-full overflow-hidden">
                                            <div
                                                className={`h-2 ${calculateSectionProgress(fields) < 33 ? 'bg-red-500' :
                                                    calculateSectionProgress(fields) < 66 ? 'bg-yellow-500' :
                                                        'bg-green-500'
                                                    }`}
                                                style={{ width: `${calculateSectionProgress(fields)}%` }}
                                            />
                                        </div>
                                        <Badge variant="secondary">
                                            {Math.round(calculateSectionProgress(fields))}%
                                        </Badge>
                                        <Badge variant="outline">
                                            {Object.keys(fields).length} Fields
                                        </Badge>
                                        {openCategories.includes(group) ? (
                                            <ChevronUp className="h-4 w-4" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4" />
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <AnimatePresence>
                                {openCategories.includes(group) && (
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: 'auto' }}
                                        exit={{ height: 0 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <CardContent className="grid gap-6 p-4">
                                            {Object.entries(fields).map(([key, value]) => (
                                                <div key={key} className="space-y-3">
                                                    <Label htmlFor={key} className="flex items-center text-sm font-medium">
                                                        {formatLabel(key)}
                                                    </Label>
                                                    <div className="flex items-start justify-between gap-4">
                                                        <p className="text-sm text-muted-foreground flex-1">
                                                            {fieldMetadata[key as keyof typeof fieldMetadata]?.howItsUsed}
                                                        </p>
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            <TooltipProvider>
                                                                {isGeneratableField(key) && (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-6 w-6"
                                                                                onClick={() => openGenerateModal(key)}
                                                                            >
                                                                                <Sparkles className="h-4 w-4" />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            Get AI suggestions
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}
                                                                {savingFields[key] && (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="h-6 w-6 flex items-center justify-center">
                                                                                <LoadingSpinner size="sm" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            Saving...
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}
                                                                {savedFields[key] && (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="h-6 w-6 flex items-center justify-center text-green-500">
                                                                                <CheckCircle2 className="h-4 w-4" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            Saved
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-6 w-6"
                                                                            onClick={() => copyToClipboard(value as string)}
                                                                        >
                                                                            <Copy className="h-4 w-4" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        Copy to clipboard
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                                <DropdownMenu>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <DropdownMenuTrigger asChild>
                                                                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                                                                    <Lightbulb className="h-4 w-4" />
                                                                                </Button>
                                                                            </DropdownMenuTrigger>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            Click to see best practices for this field
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                    <DropdownMenuContent>
                                                                        <div className="p-4 max-w-sm">
                                                                            <p className="font-semibold mb-2">Best Practices:</p>
                                                                            <p className="text-sm">{fieldMetadata[key as keyof typeof fieldMetadata]?.bestPractices}</p>
                                                                        </div>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </TooltipProvider>
                                                        </div>
                                                    </div>
                                                    {getFieldComponent(
                                                        key,
                                                        getFieldValue(localCurrentBrand, key as keyof Brand),
                                                        (value) => handleInput(key, value),
                                                        !isEditableField(key)
                                                    )}
                                                </div>
                                            ))}
                                        </CardContent>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Card>
                    ))}
                </div>
            </ScrollArea>

            <Button
                onClick={() => setShowSummaryModal(true)}
                className="mt-4"
                variant="outline"
            >
                <ClipboardCopy className="mr-2 h-4 w-4" />
                Show Brand Summary
            </Button>

            {/* Add the Brand Summary Modal */}
            <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
                <DialogContent className="sm:max-w-[800px] max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>Brand Summary</DialogTitle>
                        <DialogDescription>
                            A complete summary of all populated brand information.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <ScrollArea className="mt-4 h-[50vh] rounded-md border p-4">
                        <pre className="whitespace-pre-wrap font-mono text-sm">
                            {generateBrandSummary()}
                        </pre>
                    </ScrollArea>
                    
                    <div className="mt-4 flex justify-end">
                        <Button
                            onClick={() => {
                                const summary = generateBrandSummary();
                                copyToClipboard(summary);
                            }}
                            className="w-auto"
                        >
                            <Copy className="mr-2 h-4 w-4" />
                            Copy to Clipboard
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {showGenerateModal && (
                <AIGenerateDialog
                    fieldName={currentField as keyof Brand}
                    brand={localCurrentBrand!}
                    defaultInstructions={fieldMetadata[currentField as keyof typeof fieldMetadata]?.bestPractices || ''}
                    fieldMetadata={fieldMetadata[currentField as keyof typeof fieldMetadata] || {}}
                    existingValue={isFeedbackMode ? localCurrentBrand![currentField as keyof Brand] as string : undefined}
                    initialFeedback={isFeedbackMode ? brandGeniusFeedback : ''}
                    onApply={applyGeneratedContent}
                    onClose={() => {
                        setShowGenerateModal(false);
                        setIsFeedbackMode(false);
                        setBrandGeniusFeedback('');
                    }}
                />
            )}
            
            {/* Choice Modal when field already has a value */}
            {showChoiceModal && (
                <Dialog open={showChoiceModal} onOpenChange={setShowChoiceModal}>
                    <DialogContent className="sm:max-w-[500px] p-6 bg-white">
                        <DialogHeader>
                            <DialogTitle>How would you like Brand Genius to help?</DialogTitle>
                            <DialogDescription>
                                Choose how you want Brand Genius to assist with this field.
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="grid gap-4 py-4">
                            <Button 
                                onClick={() => {
                                    setShowChoiceModal(false);
                                    setIsFeedbackMode(false);
                                    setShowGenerateModal(true);
                                }}
                                className="w-full"
                            >
                                <Sparkles className="mr-2 h-4 w-4" />
                                Have Brand Genius generate a new answer for me
                            </Button>
                            
                            <Button 
                                onClick={() => {
                                    setShowChoiceModal(false);
                                    setIsFeedbackMode(true);
                                    setBrandGeniusFeedback('');
                                    setShowGenerateModal(true);
                                }}
                                variant="outline"
                                className="w-full"
                            >
                                <RefreshCcw className="mr-2 h-4 w-4" />
                                Give Brand Genius feedback to improve the current answer
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};